/**
 * Contact type catalog and multi-role helpers (frontend mirror of backend contactRoles.js).
 *
 * Progression: Contact → Candidate (Job Seeker) → Employee | Contractor (Independent) | Contractor (C2C)
 * Parallel: Trainer/Instructor/Teacher, Trainee, Company, Guest
 */

export const CONTACT_TYPE_DEFS = [
  { code: 'regular', label: 'Contact' },
  { code: 'candidate', label: 'Candidate (Job Seeker)' },
  { code: 'employee', label: 'Employee' },
  { code: 'contractor_independent', label: 'Contractor (Independent)' },
  { code: 'contractor_c2c', label: 'Contractor (C2C)' },
  { code: 'company', label: 'Company' },
  { code: 'trainee', label: 'Trainee' },
  { code: 'guest_signedup', label: 'Guest' },
  { code: 'trainer', label: 'Trainer / Instructor / Teacher' }
];

const LEGACY_TYPE_ALIASES = {
  contractor: 'contractor_independent'
};

const VALID_TYPES = new Set([
  ...CONTACT_TYPE_DEFS.map((d) => d.code),
  'contractor'
]);

const PRIMARY_ORDER = [
  'employee',
  'contractor_independent',
  'contractor_c2c',
  'contractor',
  'trainer',
  'trainee',
  'candidate',
  'company',
  'regular',
  'guest_signedup'
];

export const TYPE_LABELS = Object.fromEntries(CONTACT_TYPE_DEFS.map((d) => [d.code, d.label]));
TYPE_LABELS.contractor = TYPE_LABELS.contractor_independent;

export const SIGNUP_AS_OPTIONS = [
  { value: 'contact', label: 'Contact (default)' },
  { value: 'candidate', label: 'Candidate (Job Seeker)' },
  { value: 'trainer', label: 'Trainer / Instructor / Teacher' }
];

const CONTRACTOR_TYPES = new Set([
  'contractor',
  'contractor_independent',
  'contractor_c2c'
]);

function canonicalizeType(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const mapped = LEGACY_TYPE_ALIASES[s] || s;
  return VALID_TYPES.has(mapped) || VALID_TYPES.has(s) ? mapped : null;
}

export function normalizeContactRoles(roles, fallbackType) {
  const raw = Array.isArray(roles) ? roles : [];
  const out = [];
  const seen = new Set();
  for (const r of raw) {
    const s = canonicalizeType(r);
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  if (!out.length && fallbackType) {
    const fb = canonicalizeType(fallbackType);
    if (fb) out.push(fb);
  }
  if (!out.length) out.push('regular');
  if (!out.includes('regular') && !out.includes('company') && !out.includes('guest_signedup')) {
    out.unshift('regular');
  }
  return out;
}

export function primaryContactTypeFromRoles(roles) {
  const normalized = normalizeContactRoles(roles);
  for (const t of PRIMARY_ORDER) {
    const c = canonicalizeType(t) || t;
    if (normalized.includes(c) || normalized.includes(t)) {
      return canonicalizeType(t) || t;
    }
  }
  return 'regular';
}

/** Accept JSON string from some APIs / serializers so list views always see arrays. */
function parseContactRolesExtra(extra) {
  if (extra == null) return null;
  if (Array.isArray(extra)) return extra;
  if (typeof extra === 'string') {
    const t = extra.trim();
    if (!t) return null;
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function effectiveContactRoles(contact) {
  if (!contact) return ['regular'];
  const legacy = contact.contact_type || 'regular';
  const raw = contact.contact_roles ?? contact.contactRoles;
  const extra = parseContactRolesExtra(raw);
  if (extra == null) return normalizeContactRoles([], legacy);
  return normalizeContactRoles(extra, legacy);
}

export function contactHasRole(contact, type) {
  const want = canonicalizeType(type) || String(type || '');
  const roles = effectiveContactRoles(contact);
  if (roles.includes(want)) return true;
  if (CONTRACTOR_TYPES.has(want)) {
    return roles.some((r) => CONTRACTOR_TYPES.has(r));
  }
  return false;
}

export function contactIsContractor(contact) {
  return effectiveContactRoles(contact).some((r) => CONTRACTOR_TYPES.has(r));
}

export function allowedNextRoles(currentRoles) {
  const roles = normalizeContactRoles(currentRoles);
  const next = new Set();
  const isEmployment = roles.some((r) =>
    ['employee', 'contractor_independent', 'contractor_c2c', 'contractor'].includes(r)
  );
  const isCandidate = roles.includes('candidate');

  if (!isCandidate && !isEmployment) next.add('candidate');
  if (isCandidate && !isEmployment) {
    next.add('employee');
    next.add('contractor_independent');
    next.add('contractor_c2c');
  }
  if (!roles.includes('trainee')) next.add('trainee');
  if (!roles.includes('trainer')) next.add('trainer');
  if (!roles.includes('company')) next.add('company');
  return [...next].filter((c) => !roles.includes(c));
}

export const CONTACT_TYPE_OPTIONS = CONTACT_TYPE_DEFS.map((d) => ({
  code: d.code,
  label: d.label
}));
